/**
 * sharedFetcher — 같은 조회를 탭 안의 모든 훅이 함께 쓴다
 *
 * 알림 개수·HR 배지처럼 여러 화면 장치(사이드바·대시보드…)가 같은 숫자를 볼 때,
 * 장치마다 따로 조회하거나 따로 묶으면 실시간 신호 하나가 장치 수만큼 곱해진다.
 * 2026-10-15 PROD: 알림 «일괄 처리» 신호가 몰려 4초에 390회 → 사무실 IP 분당 한도(300) 초과 → HR 화면 429.
 *
 *  - request(): 신호·폴링·창 복귀 — 탭 전체에서 minIntervalMs 에 한 번(첫 신호는 바로, 몰리면 마지막 뒤 한 번 더)
 *  - fetchNow(): 사람이 방금 처리한 직후 — 바로. 이미 진행 중이면 그 조회가 끝난 «뒤» 한 번 더 묻고 그것까지 기다린다
 *    (진행 중 조회는 처리 «전» 값을 들고 올 수 있다 — Codex K-1)
 *  - 결과는 구독한 모든 장치에 같은 값으로 나눠 준다. 새로 붙은 장치는 마지막 값을 먼저 받는다.
 *  - scope(): 로그인한 사람 표식(사용자 번호). 바뀌면 마지막 값을 버리고, 이전 사람으로 시작한 조회의 결과는 버린다(Codex K-2)
 */
import throttle from 'lodash/throttle';

export function createSharedFetcher<T>(
  fetcher: () => Promise<T>,
  minIntervalMs: number,
  scope: () => string | null = () => null,
) {
  let latest: { scope: string | null; value: T } | undefined;
  let inFlight: Promise<void> | null = null;
  let rerun: Promise<void> | null = null;
  const listeners = new Set<(value: T) => void>();

  const runOnce = (): Promise<void> => {
    const startedScope = scope();
    return fetcher()
      .then((value) => {
        if (scope() !== startedScope) return; // 그사이 다른 사람으로 바뀌었다 — 버린다
        latest = { scope: startedScope, value };
        listeners.forEach((l) => l(value));
      })
      .catch(() => {
        /* 401 등은 조용히 — 다음 폴링·신호가 다시 묻는다 */
      });
  };

  const fetchNow = (): Promise<void> => {
    if (!inFlight) {
      inFlight = runOnce().finally(() => { inFlight = null; });
      return inFlight;
    }
    // 진행 중 — 끝난 뒤 한 번만 더(여러 번 불려도 한 번)
    if (!rerun) {
      rerun = inFlight.then(() => {
        rerun = null;
        return fetchNow();
      });
    }
    return rerun;
  };

  const request = throttle(() => { void fetchNow(); }, minIntervalMs);

  return {
    request,
    fetchNow,
    getLatest: (): T | undefined => (latest && latest.scope === scope() ? latest.value : undefined),
    subscribe(fn: (value: T) => void): () => void {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
    /** 시험용 — 모듈 상태를 비운다 */
    reset(): void {
      request.cancel();
      latest = undefined;
      inFlight = null;
      rerun = null;
      listeners.clear();
    },
  };
}

/**
 * 로그인 표식 — 토큰 안의 사용자 번호(id). 같은 사람의 토큰이 자동 갱신돼도 그대로라 정상 결과를 버리지 않는다(Codex L-1),
 * 로그아웃·다른 사람 로그인이면 바뀐다. 토큰을 읽지 못하면 토큰 문자열 자체를 표식으로 쓴다.
 */
export function authScope(): string | null {
  let token: string | null = null;
  try {
    token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;
  } catch {
    return null;
  }
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const id = json && (json.id || json.sub || json.userId);
    return id ? String(id) : token;
  } catch {
    return token;
  }
}
